// src/components/LoadingScreen.jsx
// Auth/init loading screen.
//
// Three states:
//   offline   — navigator.onLine is false → show "No internet connection" immediately.
//               No point spinning; the user can't reach Supabase.
//   slow      — still online but taking >4s → "Taking longer than usual…"
//   stalled   — still loading after 20s → show manual RELOAD button alongside the message.
//               We do NOT auto-reload or show "Connection issue detected" —
//               slowness is a Supabase cold-start, not a broken connection.
//
// Connection-error messaging is therefore ONLY triggered by an actual network
// condition (navigator.onLine), never by a data-query failure or timeout.

import { useState, useEffect } from 'react';

export default function LoadingScreen() {
  const [offline,    setOffline]    = useState(!navigator.onLine);
  const [slow,       setSlow]       = useState(false);
  const [showReload, setShowReload] = useState(false);

  useEffect(() => {
    const handleOffline = () => setOffline(true);
    const handleOnline  = () => setOffline(false);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online',  handleOnline);

    const slowTimer   = setTimeout(() => setSlow(true), 4000);
    const reloadTimer = setTimeout(() => setShowReload(true), 20000);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online',  handleOnline);
      clearTimeout(slowTimer);
      clearTimeout(reloadTimer);
    };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#060a0f',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
    }}>
      <style>{`
        @keyframes authPulse { 0%,100%{opacity:1} 50%{opacity:0.12} }
        @keyframes ls-spin    { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ textAlign: 'center' }}>
        {offline ? (
          /* ── Offline: real network error — the only case we show an error state ── */
          <>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#ff6b35',
              margin: '0 auto 16px',
            }} />
            <div style={{ color: '#ff6b35', fontSize: 11, letterSpacing: '.04em', marginBottom: 12 }}>
              No internet connection.
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'transparent',
                border: '1px solid #ff6b35',
                color: '#ff6b35',
                padding: '6px 18px',
                borderRadius: 4,
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                fontSize: 11,
                cursor: 'pointer',
                letterSpacing: '.08em',
              }}
            >
              RETRY
            </button>
          </>
        ) : (
          /* ── Online: spinner, slow message, then optional manual reload ── */
          <>
            <div style={{
              width: 36, height: 36,
              border: '3px solid #1a2a1f',
              borderTop: '3px solid #3ddc84',
              borderRadius: '50%',
              animation: 'ls-spin 0.8s linear infinite',
              margin: '0 auto 18px',
            }} />

            {!slow && (
              <div style={{ color: '#3ddc84', fontSize: 11, letterSpacing: '.14em' }}>
                NUVPORT_OS
              </div>
            )}

            {slow && (
              <div style={{ color: '#8b949e', fontSize: 11, letterSpacing: '.06em', marginBottom: showReload ? 12 : 0 }}>
                Taking longer than usual…
              </div>
            )}

            {showReload && (
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: 'transparent',
                  border: '1px solid #3ddc84',
                  color: '#3ddc84',
                  padding: '6px 18px',
                  borderRadius: 4,
                  fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                  fontSize: 11,
                  cursor: 'pointer',
                  letterSpacing: '.08em',
                }}
              >
                RELOAD
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
